// Section 2 — Partner Store Completion Helper
// Read-only checklist that shows a partner exactly what's left before
// they go live on the public Lifted shop. Safe to render: no mutations.
// Pulls everything from the profile + partner products that are already
// loaded in PartnerDashboard.
import React, { useMemo } from "react";
import { CheckCircle2, Circle, Clock, AlertTriangle, ExternalLink, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS = {
  done:    { label: "Live",                  cls: "text-lifted-green bg-lifted-green/10 border-lifted-green/40",   icon: CheckCircle2 },
  pending: { label: "Awaiting Admin Review", cls: "text-amber-300 bg-amber-500/10 border-amber-400/40",            icon: Clock },
  partial: { label: "In Progress",           cls: "text-sky-300 bg-sky-500/10 border-sky-400/40",                  icon: Circle },
  missing: { label: "Missing",               cls: "text-rose-300 bg-rose-500/10 border-rose-400/40",               icon: AlertTriangle },
};

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.missing;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.16em] font-bebas ${s.cls}`}>
      <Icon size={11} /> {s.label}
    </span>
  );
}

export default function PartnerCompletionChecklist({ profile, partnerSubmissions }) {
  const checks = useMemo(() => {
    const p = profile || {};
    const subs = Array.isArray(partnerSubmissions) ? partnerSubmissions : [];
    const compliance = (p.compliance && p.compliance.status) || "pending";
    const approved   = compliance === "approved";

    const hasProduct      = subs.length > 0;
    const hasProductImage = subs.some((s) => (s.images && s.images.length) || s.image);
    const hasProductPrice = subs.some((s) => (s.price_cents || s.price));
    const hasProductVariant = subs.some(
      (s) => Array.isArray(s.variants) && s.variants.length > 0,
    );

    const rows = [
      { id: "business_name", label: "Business name added", done: !!(p.business_name && p.business_name.trim() && p.business_name !== "Lifted Partner") },
      { id: "logo",          label: "Logo uploaded",       done: !!p.logo_url },
      // Location is "done" when either a street address OR a verified
      // city+state+latlng pin is present. This way partners that pin their
      // shop on the map without exposing a public street address (legitimate
      // for brands / companies) still count as complete.
      { id: "location",      label: "Location added (street OR map pin)",
        done: !!p.primary_address || !!(p.city && p.state && typeof p.lat === "number" && typeof p.lng === "number") },
      { id: "city_state",    label: "City + state added",  done: !!(p.city && p.state) },
      { id: "latlng",        label: "Map coordinates set", done: typeof p.lat === "number" && typeof p.lng === "number" },
      { id: "bio",           label: "Bio written",         done: !!(p.bio && p.bio.trim()) },
      { id: "product",       label: "At least 1 product uploaded",   done: hasProduct },
      { id: "product_image", label: "At least 1 product image",       done: hasProductImage },
      { id: "product_price", label: "Product price set",              done: hasProductPrice },
      { id: "product_variant", label: "Product variation (if needed)", done: true /* variants are optional */, hint: hasProductVariant ? "Variants present" : "Optional" },
      { id: "approval",      label: "Admin approval", status: approved ? "done" : (hasProduct ? "pending" : "missing"), done: approved },
    ];

    // Status mapping
    rows.forEach((r) => {
      if (r.status) return;
      r.status = r.done ? "done" : "missing";
    });

    const total = rows.length;
    const completed = rows.filter((r) => r.status === "done").length;
    const pct = Math.round((completed / total) * 100);

    // Storefront-level status
    let publicStatus = "missing";
    if (approved) publicStatus = "done";
    else if (rows.find((r) => r.id === "logo").done && rows.find((r) => r.id === "business_name").done) publicStatus = "pending";
    else if (completed > 0) publicStatus = "partial";

    return { rows, total, completed, pct, publicStatus };
  }, [profile, partnerSubmissions]);

  const slug = profile?.slug;
  const publicUrl = slug ? `/shop/partner/${slug}` : null;

  return (
    <section className="glass-panel p-6" data-testid="partner-completion-helper">
      <header className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="eyebrow !text-lifted-text-dim">Section 2 · Completion helper</div>
          <h2 className="font-anton text-xl uppercase text-white mt-1">Go-live checklist</h2>
          <p className="text-xs text-lifted-dim mt-1 max-w-md">
            Knock these out and your shop appears in the public partner directory on /shop.
          </p>
        </div>
        <div className="text-right">
          <div className="font-anton text-3xl text-lifted-green" data-testid="partner-completion-pct">
            {checks.pct}%
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bebas text-lifted-dim">
            {checks.completed} / {checks.total} done
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden mb-5">
        <div
          className="h-full bg-lifted-green transition-all"
          style={{ width: `${checks.pct}%` }}
          data-testid="partner-completion-bar"
        />
      </div>

      {/* Checklist */}
      <ul className="space-y-2" data-testid="partner-completion-rows">
        {checks.rows.map((r) => {
          const ok = r.status === "done";
          return (
            <li
              key={r.id}
              className="flex items-center justify-between gap-3"
              data-testid={`partner-check-${r.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {ok ? (
                  <CheckCircle2 size={14} className="text-lifted-green shrink-0" />
                ) : (
                  <Circle size={14} className="text-lifted-dim shrink-0" />
                )}
                <span className={ok ? "text-white text-sm" : "text-lifted-dim text-sm"}>
                  {r.label}
                </span>
              </div>
              <StatusPill status={r.status} />
            </li>
          );
        })}
      </ul>

      {/* Visibility & action row */}
      <div className="mt-5 pt-4 border-t border-white/5 grid sm:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bebas text-lifted-dim mb-1">
            Public shop visibility
          </div>
          <StatusPill status={checks.publicStatus} />
          <div className="text-[11px] text-lifted-dim mt-2">
            Top Products / Featured Products eligibility unlocks once your shop is live and an admin
            curates your catalog.
          </div>
        </div>
        <div className="flex items-end justify-start sm:justify-end gap-2 flex-wrap">
          {publicUrl && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost inline-flex items-center gap-1 text-xs"
              data-testid="partner-checklist-preview"
            >
              <ExternalLink size={11} /> Preview storefront
            </a>
          )}
          <Link
            to="/partner/products/submit"
            className="btn-lifted inline-flex items-center gap-1 text-xs"
            data-testid="partner-checklist-add-product"
          >
            Add product <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </section>
  );
}
